import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Tooltip } from '@chikara/ui';
import * as ROUTES from '@/shared/constants/routes';
import styles from './LandingPage.module.css';

const HELP_REQUEST_URL = 'https://ayuda.delt.ai/hc/es-419/requests/new';
const HELP_CENTER_URL = 'https://ayuda.delt.ai/hc/es-419';

const leftBackgroundUrl = new URL('../../assets/left-background.png', import.meta.url).toString();
const brandSymbolUrl = new URL('../../assets/brand-symbol.svg', import.meta.url).toString();
const infoIconUrl = new URL('../../assets/info-icon.svg', import.meta.url).toString();
const helpIconUrl = new URL('../../assets/help-icon.svg', import.meta.url).toString();

interface DocumentItem {
  id: string;
  label: string;
  tooltip?: string;
}

function openHelpRequest() {
  window.open(HELP_REQUEST_URL, '_blank', 'noopener,noreferrer');
}

export default function LandingPage() {
  const navigate = useNavigate();

  const companyDocuments = useMemo<DocumentItem[]>(
    () => [
      {
        id: 'acta-constitutiva',
        label: 'Acta Constitutiva',
        tooltip: 'Con datos de inscripción en el Registro Público de Comercio',
      },
      {
        id: 'cif',
        label: 'Cédula de Identificación Fiscal',
        tooltip: 'También puedes continuar con la Constancia de Situación Fiscal',
      },
      {
        id: 'fiel',
        label: 'Constancia de la Firma Electrónica Avanzada',
        tooltip: 'También puedes continuar con Comprobante de Certificado de Sello Digital',
      },
      { id: 'domicilio', label: 'Comprobante de domicilio no mayor a 3 meses de antigüedad' },
      { id: 'ine', label: 'Tu INE como apoderado legal de la empresa' },
      { id: 'apoderado', label: 'Acta donde te nombren como apoderado legal' },
      { id: 'estado-cuenta', label: 'Estado de cuenta bancario no mayor a dos meses de antigüedad' },
      { id: 'registro-comercio', label: 'Boleta de inscripción en el Registro Público de Comercio' },
    ],
    []
  );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Button variant="outline" size="md" onClick={() => navigate(ROUTES.LOGIN)}>
          Iniciar sesión
        </Button>
      </header>

      <div className={styles.layout}>
        <aside className={styles.leftPane} aria-hidden="true">
          <div className={styles.leftPaneInner}>
            <img src={brandSymbolUrl} alt="" className={styles.brandSymbol} />
            <div className={styles.leftText}>
              <div className={styles.leftTitle}>
                Solicita tu tarjeta de <br />
                crédito corporativa
              </div>
              <div className={styles.leftSubtitle}>
                Lleva tu control financiero, sin que te cause dolor de cabeza. Revisa tus gastos y
                obligaciones administrativas de forma sencilla.
              </div>
            </div>
          </div>
        </aside>

        <main className={styles.main}>
          <div className={styles.mainInner}>
            <div className={styles.hero}>
              <div className={styles.heroTitle}>¡Hola!</div>
              <div className={styles.heroSubtitle}>
                Para facilitar el proceso, es necesario que tengas listos los siguientes documentos:
              </div>
            </div>

            <section className={styles.card} aria-label="Documentos requeridos">
              <div className={styles.cardTitle}>Documentos de la empresa:</div>
              <ul className={styles.docList}>
                {companyDocuments.map((item) => (
                  <li key={item.id} className={styles.docListItem}>
                    <span className={styles.docItem}>
                      <span className={styles.docText}>{item.label}</span>
                      {item.tooltip ? (
                        <Tooltip content={item.tooltip}>
                          <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className={styles.infoButton}
                            aria-label="Más información"
                          >
                            <img src={infoIconUrl} alt="" className={styles.infoIcon} />
                          </Button>
                        </Tooltip>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>

              <div className={styles.cardActions}>
                <div className={styles.actions}>
                  <Button
                    variant="outline"
                    size="md"
                    className={styles.actionsLogin}
                    onClick={() => navigate(ROUTES.LOGIN)}
                  >
                    Iniciar sesión
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    className={styles.actionsRequest}
                    onClick={openHelpRequest}
                  >
                    Solicitar ahora
                  </Button>
                </div>
              </div>
            </section>
          </div>
        </main>
      </div>

      <a
        className={styles.helpLink}
        href={HELP_CENTER_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        <img src={helpIconUrl} alt="" className={styles.helpIcon} />
        <span className={styles.helpText}>Ayuda</span>
      </a>

      {/* CSS var for background image */}
      <style>{`:root{--landing-left-bg:url('${leftBackgroundUrl}');}`}</style>
    </div>
  );
}

export { LandingPage };
